"use strict";
import { defaultAbiCoder } from "@ethersproject/abi";
import { TradeType } from "@uniswap/sdk-core";
import { wagmiConfig } from "components/Web3Provider/wagmiConfig";
import { clientToProvider } from "hooks/useEthersProvider";
import ms from "ms";
import { addTransaction, finalizeTransaction, updateTransactionInfo } from "state/transactions/reducer";
import {
  TransactionType
} from "state/transactions/types";
import { isPendingTx } from "state/transactions/utils";
import { call, cancel, delay, fork, put, race, select, take } from "typed-redux-saga";
import { TransactionStatus } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { Routing } from "data/tradingApi/__generated__";
import { isL2ChainId } from "features/chains/utils";
import {
  ApprovalEditedInWalletError,
  HandledTransactionInterrupt,
  TransactionStepFailedError,
  UnexpectedTransactionStateError
} from "features/transactions/errors";
import {
  TransactionStepType
} from "features/transactions/steps/types";
import { isUniswapX } from "features/transactions/swap/utils/routing";
import { parseERC20ApproveCalldata } from "utils/approvals";
import { interruptTransactionFlow } from "utils/saga";
import { isSameAddress } from "utilities/src/addresses";
import { percentFromFloat } from "utilities/src/format/percent";
import { logger } from "utilities/src/logger/logger";
import noop from "utilities/src/react/noop";
import { currencyId } from "utils/currencyId";
import { signTypedData } from "utils/signing";
import { getBytecode, getConnectorClient, getTransaction } from "wagmi/actions";
import { isSmartContractAccountBytecode } from "features/smartWallet/delegation/isSmartContractAccount";
export function* handleSignatureStep({ setCurrentStep, step, ignoreInterrupt, account }) {
  const { throwIfInterrupted } = yield* watchForInterruption(ignoreInterrupt);
  addTransactionBreadcrumb({
    step,
    data: {
      domain: JSON.stringify(step.domain),
      values: JSON.stringify(step.values),
      types: JSON.stringify(step.types)
    }
  });
  setCurrentStep({ step, accepted: false });
  const signer = yield* call(getSigner, account.address);
  const signature = yield* call(signTypedData, signer, step.domain, step.types, step.values);
  yield* call(throwIfInterrupted);
  addTransactionBreadcrumb({ step, data: { signature }, status: "complete" });
  return signature;
}
export function* handleOnChainStep(params) {
  const { account, step, setCurrentStep, info, allowDuplicativeTx, ignoreInterrupt, onModification } = params;
  const { chainId } = step.txRequest;
  addTransactionBreadcrumb({ step, data: { ...info } });
  const duplicativeTx = yield* findDuplicativeTx(info, account, chainId, allowDuplicativeTx);
  if (duplicativeTx) {
    if (duplicativeTx.status === TransactionStatus.Confirmed) {
      addTransactionBreadcrumb({ step, data: { duplicativeTx: true, hash: duplicativeTx.hash }, status: "complete" });
      return duplicativeTx.hash;
    } else {
      addTransactionBreadcrumb({ step, data: { duplicativeTx: true, hash: duplicativeTx.hash }, status: "in progress" });
      setCurrentStep({ step, accepted: true });
      return yield* handleOnChainConfirmation(params, duplicativeTx.hash);
    }
  }
  const { throwIfInterrupted } = yield* watchForInterruption(ignoreInterrupt);
  setCurrentStep({ step, accepted: false });
  const result = yield* call(submitTransaction, params);
  const { hash, nonce, data } = result;
  setCurrentStep({ step, accepted: true });
  yield* put(addTransaction({ from: account.address, info, hash, nonce, chainId }));
  if (step.txRequest.data !== data && onModification) {
    yield* call(onModification, { hash, data, nonce });
  }
  yield* call(throwIfInterrupted);
  return yield* handleOnChainConfirmation(params, hash);
}
function* handleOnChainConfirmation(params, hash) {
  const { step, shouldWaitForConfirmation = true, ignoreInterrupt } = params;
  if (!shouldWaitForConfirmation) {
    return hash;
  }
  if (ignoreInterrupt) {
    yield* call(waitForTransaction, hash, step);
    return hash;
  }
  const { interrupt } = yield* race({
    transactionFinished: call(waitForTransaction, hash, step),
    interrupt: take(interruptTransactionFlow.type)
  });
  if (interrupt) {
    throw new HandledTransactionInterrupt("Transaction flow was interrupted");
  }
  addTransactionBreadcrumb({ step, data: { txHash: hash }, status: "complete" });
  return hash;
}
async function getTransactionOrNull(provider, hash) {
  try {
    return await provider.getTransaction(hash);
  } catch {
    return null;
  }
}
async function isSmartContractAccount(address, chainId) {
  try {
    return isSmartContractAccountBytecode(await getBytecode(wagmiConfig, { address, chainId }));
  } catch {
    return false;
  }
}
function* submitTransaction(params) {
  const { account, step } = params;
  if (!account.address) {
    throw new Error("Wallet not connected. Please connect your wallet and try again.");
  }
  const provider = yield* call(getProvider);
  const txRequest = {
    ...step.txRequest,
    from: account.address,
    value: step.txRequest.value ? `0x${BigInt(step.txRequest.value.toString()).toString(16)}` : "0x0"
  };
  try {
    const hash = yield* call([provider, "send"], "eth_sendTransaction", [txRequest]);
    let response = null;
    for (let attempt = 0; attempt < 5; attempt++) {
      yield* delay(attempt === 0 ? 500 : 2e3);
      response = yield* call(getTransactionOrNull, provider, hash);
      if (response) {
        break;
      }
    }
    if (!response) {
      const isContractAccount = yield* call(isSmartContractAccount, account.address, step.txRequest.chainId);
      if (isContractAccount) {
        return { hash, data: String(step.txRequest.data ?? "0x"), nonce: 0 };
      }
      throw new TransactionStepFailedError({ message: "Transaction not found after submission", step });
    }
    return transformTransactionResponse(response);
  } catch (error) {
    const rpcDetails = extractRpcErrorDetails(error);
    logger.error(error, {
      tags: { file: "transactions/utils", function: "submitTransaction" },
      extra: {
        code: rpcDetails.code,
        message: rpcDetails.message,
        revertReason: rpcDetails.revertReason,
        rawRevertData: rpcDetails.rawRevertData,
        txRequest
      }
    });
    if (error && typeof error === "object" && "transactionHash" in error) {
      return yield* recoverTransactionFromHash(error.transactionHash, step);
    }
    throw error;
  }
}
function extractRpcErrorDetails(error) {
  if (!error || typeof error !== "object") {
    return {};
  }
  const err = error;
  const nestedMessage = err?.data?.message ?? err?.error?.message ?? err?.error?.data?.message ?? err?.cause?.message ?? err?.body?.error?.message;
  const code = err?.code ?? err?.error?.code ?? err?.data?.code ?? err?.error?.data?.code ?? err?.cause?.code;
  const rawRevertData = findFirstHexData(err);
  const revertReason = rawRevertData ? decodeRevertReason(rawRevertData) : void 0;
  return {
    code,
    message: err?.message ?? nestedMessage,
    revertReason,
    rawRevertData
  };
}
function findFirstHexData(err) {
  const candidates = [
    err?.data?.originalError?.data,
    err?.data?.data,
    err?.data,
    err?.error?.data?.originalError?.data,
    err?.error?.data?.data,
    err?.error?.data,
    err?.body?.error?.data,
    err?.cause?.data,
    err?.body
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.startsWith("0x")) {
      return candidate;
    }
    if (candidate && typeof candidate === "object") {
      const nested = findFirstHexData(candidate);
      if (nested) {
        return nested;
      }
    }
  }
  return void 0;
}
function decodeRevertReason(data) {
  if (typeof data !== "string" || !data.startsWith("0x") || data.length < 10) {
    return void 0;
  }
  const selector = data.slice(0, 10).toLowerCase();
  const payload = `0x${data.slice(10)}`;
  try {
    if (selector === "0x08c379a0") {
      const [reason] = defaultAbiCoder.decode(["string"], payload);
      return typeof reason === "string" ? reason : void 0;
    }
    if (selector === "0x4e487b71") {
      const [panicCode] = defaultAbiCoder.decode(["uint256"], payload);
      const hexCode = BigInt(panicCode.toString()).toString(16);
      return `Panic(0x${hexCode.padStart(2, "0")})`;
    }
  } catch {
  }
  return void 0;
}
function* recoverTransactionFromHash(hash, step) {
  const transaction = yield* pollForTransaction(hash, step.txRequest.chainId);
  if (!transaction) {
    throw new TransactionStepFailedError({ message: `Transaction not found`, step });
  }
  return transformTransactionResponse(transaction);
}
function* pollForTransaction(hash, chainId) {
  const POLL_INTERVAL = 2e3;
  const MAX_POLLING_TIME = isL2ChainId(chainId) ? 12e3 : 24e3;
  let elapsed = 0;
  while (elapsed < MAX_POLLING_TIME) {
    try {
      return yield* call(getTransaction, wagmiConfig, { chainId, hash });
    } catch {
      yield* delay(POLL_INTERVAL);
      elapsed += POLL_INTERVAL;
    }
  }
  return null;
}
function transformTransactionResponse(response) {
  if ("data" in response) {
    return { hash: response.hash, data: response.data, nonce: response.nonce };
  }
  return { hash: response.hash, data: response.input, nonce: response.nonce };
}
export function* handlePermitTransactionStep(params) {
  const { step } = params;
  const info = getPermitTransactionInfo(step);
  return yield* call(handleOnChainStep, { ...params, info });
}
export function* handleApprovalTransactionStep(params) {
  const { step } = params;
  const info = getApprovalTransactionInfo(step);
  return yield* call(handleOnChainStep, {
    ...params,
    info,
    *onModification({ hash, data }) {
      const { isInsufficient, approvedAmount } = checkApprovalAmount(data, step);
      yield* put(
        updateTransactionInfo({
          chainId: step.txRequest.chainId,
          hash,
          info: { ...info, amount: approvedAmount }
        })
      );
      if (isInsufficient) {
        throw new ApprovalEditedInWalletError({ step });
      }
    }
  });
}
function getApprovalTransactionInfo(approvalStep) {
  const submittedApprovalAmount = approvalStep.type === TransactionStepType.TokenApprovalTransaction ? getSubmittedApprovalAmount(approvalStep) : void 0;
  return {
    type: TransactionType.APPROVAL,
    tokenAddress: approvalStep.token.address,
    spender: approvalStep.spender,
    amount: submittedApprovalAmount ?? approvalStep.amount
  };
}
function getSubmittedApprovalAmount(approvalStep) {
  try {
    return parseERC20ApproveCalldata(approvalStep.txRequest.data?.toString() ?? "").amount.toString(10);
  } catch {
    return void 0;
  }
}
function getPermitTransactionInfo(approvalStep) {
  return {
    type: TransactionType.PERMIT,
    tokenAddress: approvalStep.token.address,
    spender: approvalStep.spender,
    amount: approvalStep.amount
  };
}
function checkApprovalAmount(data, step) {
  const requiredAmount = BigInt(`0x${parseInt(step.amount, 10).toString(16)}`);
  const submitted = parseERC20ApproveCalldata(data);
  const approvedAmount = submitted.amount.toString(10);
  if (step.type === TransactionStepType.TokenRevocationTransaction) {
    return { isInsufficient: submitted.amount !== BigInt(0), approvedAmount };
  }
  return { isInsufficient: submitted.amount < requiredAmount, approvedAmount };
}
const RECENT_TX_DUPLICATE_WINDOW = ms("30s");
const RECENT_APPROVAL_DUPLICATE_WINDOW = ms("5m");
function isRecentTx(tx) {
  const currentTime = Date.now();
  const failed = tx.status === TransactionStatus.Failed;
  if (failed) {
    return false;
  }
  const duplicateWindow = tx.info.type === TransactionType.APPROVAL ? RECENT_APPROVAL_DUPLICATE_WINDOW : RECENT_TX_DUPLICATE_WINDOW;
  const referenceTime = tx.status === TransactionStatus.Confirmed ? tx.confirmedTime : tx.addedTime;
  return currentTime - referenceTime < duplicateWindow;
}
function* findDuplicativeTx(info, account, chainId, allowDuplicativeTx) {
  if (allowDuplicativeTx) {
    return void 0;
  }
  const transactionMap = (yield* select((state) => state.localWebTransactions[chainId])) ?? {};
  const transactionsForAccount = Object.values(transactionMap).filter((tx) => isSameAddress(tx.from, account.address));
  return transactionsForAccount.find(
    (tx) => (isPendingTx(tx) || isRecentTx(tx)) && JSON.stringify(tx.info) === JSON.stringify(info)
  );
}
export function* watchForInterruption(ignoreInterrupt = false) {
  if (ignoreInterrupt) {
    return { throwIfInterrupted: noop };
  }
  let wasInterrupted = false;
  const watchForInterruptionTask = yield* fork(function* () {
    yield* take(interruptTransactionFlow.type);
    wasInterrupted = true;
  });
  function* throwIfInterrupted() {
    if (wasInterrupted) {
      throw new HandledTransactionInterrupt("Transaction flow was interrupted");
    }
    yield* cancel(watchForInterruptionTask);
  }
  return { throwIfInterrupted };
}
function* waitForTransaction(hash, step) {
  while (true) {
    const { payload } = yield* take(finalizeTransaction.type);
    if (payload.hash === hash) {
      if (payload.status === TransactionStatus.Confirmed) {
        return;
      } else {
        throw new TransactionStepFailedError({ message: `${step.type} failed on-chain`, step });
      }
    }
  }
}
async function getProvider() {
  try {
    const client = await getConnectorClient(wagmiConfig);
    const provider = clientToProvider(client);
    if (!provider) {
      throw new UnexpectedTransactionStateError(`Failed to get provider during transaction flow`);
    }
    return provider;
  } catch (error) {
    if (error instanceof Error && error.message.includes("Connector not connected")) {
      throw new Error("Wallet not connected. Please connect your wallet and try again.");
    }
    throw error;
  }
}
export async function getSigner(account) {
  if (!account) {
    throw new Error("Wallet not connected. Please connect your wallet and try again.");
  }
  return (await getProvider()).getSigner(account);
}
export function getSwapTransactionInfo(trade) {
  if (trade.routing === Routing.BRIDGE) {
    return {
      type: TransactionType.BRIDGE,
      inputCurrencyId: currencyId(trade.inputAmount.currency),
      inputChainId: trade.inputAmount.currency.chainId,
      outputCurrencyId: currencyId(trade.outputAmount.currency),
      outputChainId: trade.outputAmount.currency.chainId,
      inputCurrencyAmountRaw: trade.inputAmount.quotient.toString(),
      outputCurrencyAmountRaw: trade.outputAmount.quotient.toString(),
      quoteId: trade.quote.requestId,
      depositConfirmed: false
    };
  }
  const slippage = percentFromFloat(trade.slippageTolerance);
  return {
    type: TransactionType.SWAP,
    inputCurrencyId: currencyId(trade.inputAmount.currency),
    outputCurrencyId: currencyId(trade.outputAmount.currency),
    isUniswapXOrder: isUniswapX(trade),
    ...trade.tradeType === TradeType.EXACT_INPUT ? {
      tradeType: TradeType.EXACT_INPUT,
      inputCurrencyAmountRaw: trade.inputAmount.quotient.toString(),
      expectedOutputCurrencyAmountRaw: trade.outputAmount.quotient.toString(),
      minimumOutputCurrencyAmountRaw: trade.minimumAmountOut(slippage).quotient.toString()
    } : {
      tradeType: TradeType.EXACT_OUTPUT,
      maximumInputCurrencyAmountRaw: trade.maximumAmountIn(slippage).quotient.toString(),
      outputCurrencyAmountRaw: trade.outputAmount.quotient.toString(),
      expectedInputCurrencyAmountRaw: trade.inputAmount.quotient.toString()
    }
  };
}
export function addTransactionBreadcrumb(_options) {
}
